import {
  Activity,
  AlertTriangle,
  Award,
  ClipboardList,
  FileText,
  Gauge,
  LayoutGrid,
  Map as MapIcon,
  Radar,
  Settings,
  ShieldAlert,
  Siren,
  Wind,
  type LucideIcon, GraduationCap } from "lucide-react";
import type { FeatureKey } from "@/shared/auth/features";
import type { Role } from "@/shared/auth/store";

/**
 * navItems — one definition of the product's navigation.
 *
 * The desktop sidebar and the mobile "더보기" sheet render the same menu, so
 * it lives here rather than inside either of them. Adding a page means
 * touching this file and the router, nothing else.
 *
 * Grouping follows the operator's incident loop — 감지(무슨 값이 잡히나) →
 * 판단(위험한가·번지나) → 대응(무엇을 눌러 지키나) → 분석(무슨 일이었나).
 * The role-gated `minRole` on each item makes the four operational groups
 * stack into a role waterline (현장작업자→…→플랫폼관리자) on their own.
 *
 * The trailing 설정·관리 group is a holding area until the Settings hub
 * (Phase 2) folds it behind `/settings`; it is deliberately last and
 * greyed (`--nav-admin`) so it never competes with the operational rail.
 */

export interface NavItem {
  to: string;
  /** i18n key under the `nav` namespace. */
  labelKey: string;
  icon: LucideIcon;
  /** Ctrl/⌘ + N hint, shown only where there is room. */
  hint?: string;
  /** Minimum role required to see this item (mirrors the route gate). */
  minRole?: Role;
  /** 조직 기능 플래그가 켜져 있을 때만 보인다(라우트의 `RequireFeature` 와 짝). */
  feature?: FeatureKey;
}

export interface NavGroup {
  /** i18n key under the `nav` namespace. */
  titleKey: string;
  /**
   * Token name for this group's icon tint. Identity, not status — see the
   * note on --nav-* in tokens.css.
   */
  tone: string;
  items: NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    // 감지 — 지금 무슨 값이 어디서 잡히는가. 매 교대 첫 화면.
    titleKey: "nav.groupDetect",
    tone: "--nav-monitor",
    items: [
      // 관제 화면(NOC)은 통합 관제에 흡수됨 — 전체화면 버튼이 벽면 표시를 대신한다.
      { to: "/",        labelKey: "nav.controlCenter", icon: LayoutGrid, hint: "1" },
      { to: "/sites",   labelKey: "nav.sites",         icon: MapIcon,    hint: "2", minRole: "operator" },
      // 기상스테이션은 센서 노드의 한 타입으로 흡수 — 별도 메뉴를 두지 않는다.
      { to: "/sensors", labelKey: "nav.sensors",       icon: Activity,   hint: "3" },
      // CCTV — 현장 미설치(2026-09-30). 2026-10-06 사용자 지시 「cctv는 메뉴에서 빼」 — 메뉴에서 뺐다.
      // 화면(/cameras)과 기능 사용(cctv) 설정은 그대로 두므로, 다시 넣을 때는 이 줄만 되살리면 된다.
      // { to: "/cameras", labelKey: "nav.cameras", icon: Camera (lucide-react), minRole: "operator", feature: "cctv" },
    ],
  },
  {
    // 판단 — 이 값이 위험한가·번지는가. RI 산출물 계층.
    titleKey: "nav.groupAssess",
    tone: "--nav-analysis",
    items: [
      { to: "/risk",       labelKey: "nav.risk",       icon: ShieldAlert, minRole: "operator" },
      { to: "/anomaly",    labelKey: "nav.anomaly",    icon: Radar,       minRole: "operator" },
      // 확산예측이 3D 디지털트윈을 흡수 — /twin 라우트는 북마크용으로 존치(메뉴 없음).
      { to: "/dispersion", labelKey: "nav.dispersion", icon: Wind,        minRole: "safety_manager" },
    ],
  },
  {
    // 대응 — 무엇을 눌러 사람을 지키는가. 경보→대피→차단 순.
    titleKey: "nav.groupRespond",
    tone: "--nav-control",
    items: [
      { to: "/alerts",     labelKey: "nav.alerts",     icon: AlertTriangle, hint: "4" },
      { to: "/evacuation", labelKey: "nav.evacuation", icon: Gauge },
      // 작업 입·퇴실 기록(2026-10-27) — 현장 PC [작업 입실]·[작업 완료 퇴실] 기록. 위치가 아니라 출입 기록이다.
      { to: "/work-entries", labelKey: "nav.workEntries", icon: ClipboardList, minRole: "operator" },
      // 현장 장치(액추에이터) — 실시간 제어 탭은 Phase 3. 열람=관제요원 이상.
      { to: "/devices",    labelKey: "nav.devices",    icon: Siren, minRole: "safety_manager" },
      { to: "/scenarios",  labelKey: "nav.drill",      icon: GraduationCap, minRole: "operator" },
    ],
  },
  {
    // 분석 — 무슨 일이었나·증거는. 사후 통계·감사.
    titleKey: "nav.groupAnalysis",
    tone: "--nav-admin",
    items: [
      // 협약 KPI 달성 현황 — 최종평가의 관문이라 분석 그룹 첫 항목.
      { to: "/kpi",      labelKey: "nav.kpi",      icon: Award,     minRole: "org_admin" },
      // 통계개요(/overview)는 2026-09-23 보고서 화면의 「현황」 탭으로 합쳤다 —
      // 위험도·이상탐지·경보 숫자를 모아 놓은 화면이 둘이면 어느 쪽이 정본인지 모른다.
      // /twin 은 확산예측(/dispersion)과 같은 화면(variant 만 다름) — 메뉴 중복이라 2026-09-08 제거, 라우트는 북마크용 존치.
      { to: "/reports",  labelKey: "nav.reports",  icon: FileText,  minRole: "safety_manager" },
    ],
  },
  {
    // 설정 진입점 — 관리(가스DB·도면·공간캡처·시나리오·사용자·조직)는 /settings
    // 허브 뒤로 접었다. 제목 없는 그룹 = 레일 하단 구분선 + 설정 톱니 하나.
    titleKey: "",
    tone: "--nav-admin",
    items: [
      { to: "/settings", labelKey: "nav.settings", icon: Settings, minRole: "operator" },
    ],
  },
] as const;

/** Routes promoted to the mobile bottom tab bar; the rest go in the sheet. */
export const MOBILE_TABS = ["/", "/alerts", "/sensors"] as const;
